
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { useExcelParams, useMenores, useSearchFilter } from "@/composable";
import { useAdminComarca } from "@/composable/useAdminComarca";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { SortProps, sortFn } from "@/core/helpers/sort";
import Page from "@/layout/Page.vue";
import { AuthModule, MenorModule, OrganismosModule } from "@/store/modules";
import {
  computed,
  defineComponent,
  onActivated,
  onMounted,
  ref,
  watch,
} from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export default defineComponent({
  name: "menores-listado",
  components: { "download-excel": JsonExcel, Page },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const authModule = getModule(AuthModule, store);
    const comarcaId = authModule.user.comarcaId;
    const isAdmin = !comarcaId;

    const menorModule = getModule(MenorModule, store);
    const { menores, isLoading, error } = useMenores();

    const organismosModule = getModule(OrganismosModule, store);
    const { selectedComarcaId } = useAdminComarca();
    const loadingComarcas = ref(false);

    const ensureComarcasLoaded = async () => {
      if (organismosModule.organismosListado.length === 0) {
        loadingComarcas.value = true;
        try {
          await organismosModule.fetchAll();
        } finally {
          loadingComarcas.value = false;
        }
      }
    };

    const comarcas = computed(() => {
      const list = organismosModule.organismosListado.map((item) => ({
        id: item.id,
        nombre: item.nombre,
      }));
      list.unshift({ id: "TODAS", nombre: "Todas" });
      return list;
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const sortedCollection = computed(() => {
      return menores.value.slice().sort(sortFn(sortProps));
    });

    // Filter
    const { search, filteredCollection } = useSearchFilter(sortedCollection, [
      "nombreComarca",
      "responsable",
      "familiaDni",
      "nombreCompleto",
      "fechaNacimientoString",
    ]);

    // Export
    const { labels, fields, generateName } = useExcelParams(
      {
        ...(!comarcaId && { nombreComarca: t("csv.listados.menor.comarca") }),
        tratamientoValue: t("csv.listados.menor.tratamiento"),
        nombreCompleto: t("csv.listados.menor.menor"),
        fechaNacimientoString: t("csv.listados.menor.fechaNacimiento"),
        responsable: t("csv.listados.menor.responsable"),
        familiaDni: t("csv.listados.menor.familiaDni"),
        familiaEmail: t("csv.listados.familias.email"),
        familiaTelefono: t("csv.listados.familias.telefono"),
        familiaDireccion: t("csv.listados.familias.direccion"),
        familiaLocalidad: t("csv.listados.familias.localidad"),
        familiaProvincia: t("csv.listados.familias.provincia"),
        familiaPrioritaria: t(
          "tabla.serviciosSolicitadosListado.cabecera.familiaPrioritaria"
        ),
        familiaIngresos: t("formularios.familiaDetalle.ingresos.label"),
      },
      "menoresListado"
    );

    const { result, currentPage, lastPage } = useArrayPagination(
      filteredCollection,
      { pageSize: 100 }
    );

    const fetchData = async () => {
      if (isAdmin && !selectedComarcaId.value) {
        menorModule.setData([]);
        isLoading.value = false;
        error.value = null;
        search.value = "";
        currentPage.value = 1;
        return;
      }

      let idToFetch: string | undefined;
      if (isAdmin) {
        idToFetch =
          selectedComarcaId.value === "TODAS"
            ? undefined
            : selectedComarcaId.value!;
      } else {
        idToFetch = comarcaId;
      }

      isLoading.value = true;
      error.value = null;
      try {
        await menorModule.fetchAll(idToFetch);
        search.value = "";
        currentPage.value = 1;
      } catch (err) {
        error.value = err;
      } finally {
        isLoading.value = false;
      }
    };

    if (isAdmin) {
      watch(selectedComarcaId, async () => {
        await fetchData();
      });
    }

    onActivated(async () => {
      if (isAdmin) {
        await ensureComarcasLoaded();
      }
      await fetchData();
    });

    const onComarcaVisibleChange = (open: boolean) => {
      if (open && isAdmin) {
        ensureComarcasLoaded();
      }
    };

    onMounted(() => {
      setCurrentPageBreadcrumbs(t("menu.menores"), [t("menu.personas")]);
    });

    return {
      isLoading,
      error,
      menores: filteredCollection,
      handleEdit: (index, row) => router.push(`menores/${row.id}`),
      isAdmin,
      labels,
      fields,
      generateName,
      search,
      fetch: fetchData,
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange: (page: number) => (currentPage.value = page),
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      selectedComarcaId,
      comarcas,
      loadingComarcas,
      onComarcaVisibleChange,
    };
  },
});
